import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Target, Compass, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import Icon from "@/components/Icon";
import { COMPANY, IMAGES, VALUES } from "@/data/site";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function About() {
  return (
    <div data-testid="page-about">
      <PageHero
        crumb="About Us"
        eyebrow="About Us"
        title="Building a Greener Future for Indonesia"
        desc="PT Aneka Jaringan Energy (AJE) is a renewable energy EPC company based in North Jakarta, operating as part of the Malaysia-based group Aneka Jaringan Holdings Berhad."
        image={IMAGES.engineer3}
      />

      {/* STORY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <img
              src={IMAGES.projectBSD}
              alt="Proyek PLTS AJE"
              className="rounded-3xl object-cover shadow-2xl"
            />
            <div className="absolute -bottom-6 -right-6 hidden sm:block rounded-2xl bg-emerald-600 px-6 py-5 text-white shadow-xl">
              <div className="font-display text-3xl font-extrabold">120+</div>
              <div className="text-sm">Completed Projects</div>
            </div>
          </motion.div>

          <div>
            <p className="aje-eyebrow mb-3">Our Story</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Holistic, Technical, and Sustainability-Oriented
            </h2>
            <div className="mt-5 space-y-4 text-lg leading-relaxed text-slate-600">
              <p>
                We are committed to serving society, communities, and the public interest by delivering holistic solutions for a greener planet — leveraging technology, an experienced team, and driving economic growth for all.
              </p>
              <p>
                As part of the Aneka Jaringan Holdings Berhad group, PT Aneka Jaringan Energy offers end-to-end services ranging from EPCC (Engineering, Procurement, Construction & Commissioning) and BOOT schemes to Power Purchase Agreements (PPA) for solar, biomass, and biogas projects.
              </p>
            </div>
            <Link
              to="/kontak"
              data-testid="about-contact-link"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-blue-900 px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-blue-800 hover:-translate-y-0.5"
            >
              Contact Our Team <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* VISI MISI */}
      <section className="bg-slate-50 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-6 md:grid-cols-2">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-blue-900 text-white">
              <Target className="h-6 w-6" />
            </span>
            <h3 className="mt-5 font-display text-2xl font-bold text-slate-900">Vision</h3>
            <p className="mt-3 text-lg leading-relaxed text-slate-600">
              To become the leading renewable energy EPC company in Indonesia that
              drives the transition towards clean and sustainable energy.
            </p>
          </motion.div>
          <motion.div
            variants={fadeUp}
            custom={1}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-600 text-white">
              <Compass className="h-6 w-6" />
            </span>
            <h3 className="mt-5 font-display text-2xl font-bold text-slate-900">Mission</h3>
            <p className="mt-3 text-lg leading-relaxed text-slate-600">
              To provide high-efficiency, safe, and sustainable solar PV systems — from small-scale solar home systems to large-scale megawatt-level power plants, tailored to local conditions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* VALUES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <SectionHeading
          center
          eyebrow="Core Values"
          title="Four principles that drive us"
          desc="The foundation that guides every decision and project execution at AJE."
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v, i) => (
            <motion.div
              key={v.title}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="aje-card-hover rounded-2xl border border-slate-100 bg-white p-7 shadow-sm"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-900 to-emerald-600 text-white">
                <Icon name={v.icon} className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-display text-lg font-bold text-slate-900">
                {v.titleId}
              </h3>
              <span className="text-xs font-semibold uppercase tracking-widest text-emerald-600">
                {v.title}
              </span>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-[#0B132B] relative overflow-hidden py-20 sm:py-28">
        <div className="absolute inset-0 aje-grid-bg opacity-25" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <SectionHeading
              light
              eyebrow="Why AJE"
              title="Advantages You Can Rely On"
              desc="A combination of technical expertise, high-quality components, and responsive after-sales service."
            />
            <ul className="mt-8 space-y-4">
              {[
                "Superior engineering & precise technical design",
                "High-quality solar panels & components with long-term warranties",
                "Inverter with official manufacturer warranty",
                "24/7 maintenance support & monitoring",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
                  <span className="text-slate-200">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <motion.img
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            src={IMAGES.engineer2}
            alt="AJE Technician"
            className="rounded-3xl object-cover shadow-2xl"
          />
        </div>
      </section>
    </div>
  );
}
